/**
 * The agency's own view of its event share links.
 *
 * Read-only by design: MCC staff decide which events an agency promotes and
 * generate the links. What an agency needs here is the link itself, in a form
 * that is easy to copy into a WeChat post or a newsletter, and an honest count
 * of what it earned.
 *
 * Two numbers, deliberately labelled in plain words rather than in analytics
 * jargon: **people** (repeat visits from the same person folded together) and
 * **registered**. Cancelled registrations are not counted.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { getMyAgencyEventLinks } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Link2 } from 'lucide-react';
import { EmptyState, formatDate, safeList } from './AgencyPortalShared';

function Tile({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-[rgba(6,43,26,0.10)] bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#062B1A]/50">{label}</p>
      <p className="mt-1 text-xl font-semibold text-[#062B1A]">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-[#062B1A]/45">{hint}</p>}
    </div>
  );
}

export default function AgencyEventLinks() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await getMyAgencyEventLinks();
        if (cancelled) return;
        setItems(safeList(data?.items));
        setSummary(data?.summary || {});
      } catch (error) {
        console.error(error);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Active links first: a retired link is history, not something to share today.
  const ordered = useMemo(
    () => [...items].sort((a, b) => (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1)),
    [items],
  );

  const copy = (value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    toast.success('Link copied');
  };

  return (
    <div className="animate-fade-in space-y-5">
      <div className="space-y-1">
        <h2
          className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          <Link2 className="h-6 w-6 text-[#D4AF37]" />
          Event links
        </h2>
        <p className="text-sm text-[#062B1A]/60">
          Share these links to promote MCC events. Everyone who registers through one of them is
          credited to you.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Links" value={summary.links ?? 0} hint={`${summary.active_links ?? 0} active`} />
        <Tile label="Clicks" value={summary.click_count ?? 0} />
        <Tile label="People" value={summary.unique_click_count ?? 0} hint="Repeat visits folded in" />
        <Tile label="Registered" value={summary.registration_count ?? 0} />
      </div>

      {loading ? (
        <Card>
          <CardContent className="space-y-3 p-5">
            {[0, 1, 2].map((row) => (
              <div key={row} className="h-16 animate-pulse rounded bg-[#F1F3F5]" />
            ))}
          </CardContent>
        </Card>
      ) : ordered.length === 0 ? (
        <EmptyState icon={Link2} title="No event links yet">
          When MCC creates a link for you, it appears here with its clicks and registrations.
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {ordered.map((link) => (
            <Card key={link.id} className="border border-[rgba(6,43,26,0.10)]">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-base text-[#062B1A]">
                    {link.event_title || 'MCC event'}
                  </CardTitle>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                      link.status === 'active'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-50 text-slate-600'
                    }`}
                  >
                    {link.status === 'active' ? 'Active' : 'Retired'}
                  </span>
                </div>
                <p className="text-xs text-[#062B1A]/55">
                  {[link.channel_label, link.label].filter(Boolean).join(' · ')}
                  {link.event_start_at ? ` · ${formatDate(link.event_start_at)}` : ''}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 rounded-lg bg-[#F7F8FA] p-2">
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-[#062B1A]">
                    {link.share_url}
                  </span>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => copy(link.share_url)}
                    className="shrink-0 border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                  >
                    <Copy className="mr-1 size-3.5" /> Copy
                  </Button>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-[#062B1A]/70">
                  <span><strong className="text-[#062B1A]">{link.click_count}</strong> clicks</span>
                  <span><strong className="text-[#062B1A]">{link.unique_click_count}</strong> people</span>
                  <span>
                    <strong className="text-[#062B1A]">{link.active_registration_count}</strong> registered
                  </span>
                </div>
                {link.status !== 'active' && (
                  <p className="text-xs text-[#062B1A]/50">
                    This link still opens the event page, but new visits are no longer counted.
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
